import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState } from '@ui-kit';
import { useMenuTree } from '../hooks/useMenuTree';
import { useMenuDetail } from '../hooks/useMenuDetail';
import { useCreateMenu } from '../hooks/useCreateMenu';
import { useUpdateMenu } from '../hooks/useUpdateMenu';
import { useDeleteMenu } from '../hooks/useDeleteMenu';
import { useReorderMenus } from '../hooks/useReorderMenus';
import { MenuTree } from '../components/MenuTree';
import { MenuForm } from '../components/MenuForm';
import { MenuDeleteConfirm } from '../components/MenuDeleteConfirm';
import { MenuCreateDialog } from '../components/MenuCreateDialog';
import { MenuPreviewDrawer } from '../components/MenuPreviewDrawer';
import type { MenuTreeNode } from '../types/menu';
import type { CreateMenuFormValues } from '../schema/menuSchema';
import type { UpdateMenuFormValues } from '../schema/menuSchema';
import type { ApiError } from '../types/menu';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import { useHasRole } from '../../_shared/hooks/useCurrentUser';

/* ─────────────────── Skeleton ─────────────────── */
function TreeSkeleton() {
  const { t } = useTranslation();
  return (
    <div aria-busy="true" aria-label={t('menu.tree.loading')}>
      {Array.from({ length: 7 }).map((_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-2) var(--spacing-3)',
            paddingLeft: `calc(var(--spacing-3) + ${(i % 3) * 16}px)`,
          }}
        >
          <div
            style={{
              height: 14,
              width: `${40 + (i % 5) * 12}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </div>
      ))}
    </div>
  );
}

function FormSkeleton() {
  const { t } = useTranslation();
  return (
    <div aria-busy="true" aria-label={t('menu.form.title.edit')}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} style={{ marginBottom: 'var(--spacing-4)' }}>
          <div
            style={{
              height: 12,
              width: '30%',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
              marginBottom: 6,
            }}
          />
          <div
            style={{
              height: 36,
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </div>
      ))}
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const btnSecondary: React.CSSProperties = {
  ...btnPrimary,
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
};

/* ─────────────────── Page ─────────────────── */

/**
 * MenuManagementPage — /menus
 * Split layout: left tree (트리) + right form/preview (폼/프리뷰).
 * 5상태: loading / empty / error / dialog / default
 */
export function MenuManagementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // EXEC 샌드박스 모드 판정: ROLE_EXEC 보유 + ROLE_ADMIN 미보유
  const isExec = useHasRole('ROLE_EXEC');
  const isAdmin = useHasRole('ROLE_ADMIN');
  const isExecSandbox = isExec && !isAdmin;

  // Right panel selection
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createError, setCreateError] = useState<string | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<MenuTreeNode | null>(null);
  const [deleteError, setDeleteError] = useState<{ code?: string; message?: string } | undefined>();
  const [previewOpen, setPreviewOpen] = useState(false);

  // Form save error
  const [saveError, setSaveError] = useState<string | undefined>();

  // Queries
  const treeQuery = useMenuTree();
  const detailQuery = useMenuDetail(selectedId);

  // Mutations
  const createMutation = useCreateMenu();
  const updateMutation = useUpdateMenu();
  const deleteMutation = useDeleteMenu();
  const reorderMutation = useReorderMenus();

  const treeNodes = treeQuery.data ?? [];

  /* ── Handlers ── */
  const handleSelectNode = useCallback((node: MenuTreeNode) => {
    setSelectedId(node.id);
    setSaveError(undefined);
  }, []);

  const handleDeleteRequest = useCallback((node: MenuTreeNode) => {
    setDeleteTarget(node);
    setDeleteError(undefined);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    setDeleteError(undefined);
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        if (selectedId === deleteTarget.id) setSelectedId(null);
      },
      onError: (err: unknown) => {
        const apiErr = err as ApiError;
        const code = apiErr.code;
        const message = code === 'MENU-423-001'
          ? t('menuMgmt.error.MENU-423-001')
          : apiErr.message;
        setDeleteError({ code, message });
      },
    });
  }, [deleteTarget, deleteMutation, selectedId]);

  const handleCreateSubmit = useCallback(
    (values: CreateMenuFormValues) => {
      setCreateError(undefined);
      createMutation.mutate(
        {
          menuKey: values.menuKey,
          nameKo: values.nameKo,
          nameEn: values.nameEn,
          labelKey: values.labelKey,
          url: values.url,
          icon: values.icon,
          menuType: values.menuType,
          parentId: values.parentId ?? null,
          sortOrder: values.sortOrder,
          visible: values.visible ?? true,
          requiredRole: values.requiredRole || undefined,
          description: values.description,
        },
        {
          onSuccess: (data) => {
            setCreateDialogOpen(false);
            setSelectedId(data.id);
          },
          onError: (err: unknown) => {
            const apiErr = err as ApiError;
            const createErrorMessages: Record<string, string> = {
              'MENU-409-001': t('menu.error.MENU-409-001'),
              'MENU-409-003': t('menu.error.MENU-409-003'),
              'MENU-422-001': t('menu.error.MENU-422-001'),
            };
            setCreateError(
              createErrorMessages[apiErr.code] ?? apiErr.message ?? t('menu.error.loadFailed')
            );
          },
        }
      );
    },
    [createMutation, t]
  );

  const handleUpdateSubmit = useCallback(
    (values: UpdateMenuFormValues) => {
      if (!selectedId) return;
      setSaveError(undefined);
      updateMutation.mutate(
        { id: selectedId, body: values },
        {
          onSuccess: () => {
            setSaveError(undefined);
          },
          onError: (err: unknown) => {
            const apiErr = err as ApiError;
            const errorMessages: Record<string, string> = {
              'MENU-403-001': t('menu.error.MENU-403-001'),
              'MENU-404-001': t('menu.error.MENU-404-001'),
              'MENU-409-003': t('menu.error.MENU-409-003'),
              'MENU-422-001': t('menu.error.MENU-422-001'),
              'MENU-423-001': t('menuMgmt.error.MENU-423-001'),
            };
            setSaveError(
              errorMessages[apiErr.code] ?? apiErr.message ?? t('menu.error.loadFailed')
            );
          },
        }
      );
    },
    [selectedId, updateMutation, t]
  );

  const handleReorder = useCallback(
    (parentId: number | null, items: { id: number; sortOrder: number }[]) => {
      reorderMutation.mutate({ parentId, items });
    },
    [reorderMutation]
  );

  /* ── Render states ── */
  const isTreeLoading = treeQuery.isLoading;
  const isTreeError = treeQuery.isError;
  const isEmpty = !isTreeLoading && !isTreeError && treeNodes.length === 0;
  const showTree = !isTreeLoading && !isTreeError && treeNodes.length > 0;

  const isFormLoading = !!selectedId && detailQuery.isLoading;
  const isFormError = !!selectedId && detailQuery.isError;
  const showForm = !!selectedId && !isFormLoading && !isFormError && !!detailQuery.data;

  return (
    <div style={{ padding: 0 }}>
      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* Page Header */}
      <PageHeader
        title={t('menu.page.title')}
        breadcrumb={<span>{t('menu.page.breadcrumb')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              style={btnSecondary}
              aria-label={t('menu.preview.title')}
            >
              {t('menu.preview.button')}
            </button>
            {/* ButtonGuard: MANAGER lacks MENU_WRITE → disabled per #324 */}
            <ButtonGuard requirePermission="MENU_WRITE">
              {({ disabled, reason }) => (
                <button
                  type="button"
                  onClick={() => {
                    if (disabled) return;
                    setCreateError(undefined);
                    setCreateDialogOpen(true);
                  }}
                  disabled={disabled}
                  title={reason}
                  style={{
                    ...btnPrimary,
                    ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                  aria-label={t('menu.action.addMenu')}
                  aria-disabled={disabled}
                >
                  {t('menu.action.addMenu')}
                </button>
              )}
            </ButtonGuard>
          </div>
        }
      />

      {/* EXEC 샌드박스 배너 */}
      {isExecSandbox && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: 'color-mix(in srgb, var(--color-warning, #f59e0b) 12%, var(--color-surface-muted))',
            border: '1px solid color-mix(in srgb, var(--color-warning, #f59e0b) 40%, transparent)',
            borderRadius: 'var(--radius-md)',
            marginTop: 'var(--spacing-3)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>{t('menuMgmt.sandbox.exec.banner')}</span>
        </div>
      )}

      {/* Split layout */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 360px) 1fr',
          gap: 0,
          minHeight: 'calc(100vh - 160px)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          marginTop: 'var(--spacing-4)',
          background: 'var(--color-surface-background)',
        }}
        // Responsive: single column on mobile
        className="menu-split-layout"
      >
        {/* ── Left: Tree Panel ── */}
        <div
          style={{
            borderRight: '1px solid var(--color-surface-border)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: 'var(--color-surface-muted)',
          }}
        >
          {/* Tree header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-3) var(--spacing-3)',
              borderBottom: '1px solid var(--color-surface-border)',
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700,
                color: 'var(--color-text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                flex: 1,
              }}
            >
              {t('menu.tree.title')}
            </span>
            {treeNodes.length > 0 && (
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {t('menu.page.subtitle', { total: treeNodes.reduce((acc, n) => acc + 1 + (n.children?.length ?? 0) + n.children?.reduce((a, c) => a + 1 + (c.children?.length ?? 0), 0), 0) })}
              </span>
            )}
          </div>

          {/* Tree body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-2)' }}>
            {/* loading */}
            {isTreeLoading && <TreeSkeleton />}

            {/* error */}
            {isTreeError && (
              <div style={{ padding: 'var(--spacing-3)' }}>
                <ErrorState
                  title={t('menu.tree.error.title')}
                  message={
                    (treeQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                      ? t('menu.error.AUTH-403-001')
                      : ((treeQuery.error as unknown as ApiError)?.message ?? t('menu.error.network'))
                  }
                  onRetry={() => void treeQuery.refetch()}
                />
              </div>
            )}

            {/* empty */}
            {isEmpty && (
              <div style={{ padding: 'var(--spacing-4)' }}>
                <EmptyState
                  title={t('menu.tree.empty.title')}
                  description={t('menu.tree.empty.description')}
                  action={
                    <ButtonGuard requirePermission="MENU_WRITE">
                      {({ disabled, reason }) => (
                        <button
                          type="button"
                          onClick={() => {
                            if (disabled) return;
                            setCreateError(undefined);
                            setCreateDialogOpen(true);
                          }}
                          disabled={disabled}
                          title={reason}
                          style={{
                            ...btnPrimary,
                            ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                          }}
                        >
                          {t('menu.action.addMenu')}
                        </button>
                      )}
                    </ButtonGuard>
                  }
                />
              </div>
            )}

            {/* default — tree */}
            {showTree && (
              <MenuTree
                nodes={treeNodes}
                selectedId={selectedId}
                onSelect={handleSelectNode}
                onDeleteRequest={handleDeleteRequest}
                onReorder={handleReorder}
                isReordering={reorderMutation.isPending}
                isExecSandbox={isExecSandbox}
              />
            )}
          </div>
        </div>

        {/* ── Right: Form/Preview Panel ── */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: 'var(--color-surface-background)',
          }}
        >
          <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-6)' }}>
            {/* No selection — R7-075: show context-aware hint text */}
            {!selectedId && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: 300,
                  gap: 'var(--spacing-3)',
                  color: 'var(--color-text-muted)',
                }}
                aria-live="polite"
                aria-label={t('menu.form.title.edit')}
              >
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                  style={{ opacity: 0.3 }}
                >
                  <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                  <rect x="9" y="3" width="6" height="4" rx="1" />
                  <line x1="9" y1="12" x2="15" y2="12" />
                  <line x1="9" y1="16" x2="12" y2="16" />
                </svg>
                <span style={{ fontSize: 'var(--font-size-sm)' }}>
                  {treeNodes.length > 0
                    ? t('menu.tree.selectHint')
                    : t('menu.tree.empty.description')}
                </span>
              </div>
            )}

            {/* Form loading skeleton */}
            {isFormLoading && <FormSkeleton />}

            {/* Form error */}
            {isFormError && selectedId && (
              <ErrorState
                title={t('menu.error.loadFailed')}
                message={(detailQuery.error as unknown as ApiError)?.message ?? t('menu.error.network')}
                onRetry={() => void detailQuery.refetch()}
              />
            )}

            {/* Form — default state */}
            {showForm && detailQuery.data && (
              <MenuForm
                menu={detailQuery.data}
                treeNodes={treeNodes}
                onSave={handleUpdateSubmit}
                onDelete={() => {
                  const treeNode = findNodeById(treeNodes, selectedId!);
                  if (treeNode) {
                    setDeleteTarget(treeNode);
                    setDeleteError(undefined);
                  }
                }}
                isSaving={updateMutation.isPending}
                saveError={saveError}
                isExecSandbox={isExecSandbox}
              />
            )}
          </div>
        </div>
      </div>

      {/* Responsive style */}
      <style>{`
        @media (max-width: 768px) {
          .menu-split-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      {/* ── Dialogs ── */}

      {/* Create dialog */}
      <MenuCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
        errorMessage={createError}
        treeNodes={treeNodes}
      />

      {/* Delete confirm */}
      <MenuDeleteConfirm
        open={!!deleteTarget}
        node={deleteTarget}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteError(undefined);
        }}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteMutation.isPending}
        errorCode={deleteError?.code}
        errorMessage={deleteError?.message}
      />

      {/* Preview drawer */}
      <MenuPreviewDrawer
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        treeNodes={treeNodes}
      />

      {/* Access denied fallback (ROLE_USER tries to access /menus) */}
      {isTreeError && (treeQuery.error as unknown as ApiError)?.code === 'AUTH-403-001' && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--color-surface-background)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div style={{ textAlign: 'center', maxWidth: 400 }}>
            <ErrorState
              title={t('menu.error.AUTH-403-001')}
              message={t('menu.error.AUTH-403-001')}
            />
            <button
              type="button"
              onClick={() => navigate('/_index')}
              style={{ ...btnPrimary, marginTop: 'var(--spacing-4)' }}
            >
              {t('menu.action.goHome')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** 트리에서 ID 로 노드 찾기 */
function findNodeById(nodes: MenuTreeNode[], id: number): MenuTreeNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children) {
      const found = findNodeById(n.children, id);
      if (found) return found;
    }
  }
  return null;
}
